import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { setItemWithExpiry } from '../../utils/storage';
import IconButton from '@mui/material/IconButton';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    username: '',
    role: '',
  });

  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [themeMode, setThemeMode] = useState('dark');

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const { email, name, username, role } = formData;
    if (!email || !name || !username || !role) {
      setError('All fields are required.');
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(
        'https://api.dmaverix.com/api/v2/auth/register',
        formData
      );

      if (data.success) {
        setResponse(data);
        setError(null);

        if (data.tempID) {
          setItemWithExpiry('tempID', data.tempID, 600000);
        }

        navigate('/complete-registration');
      } else {
        setError('Registration failed: ' + data.message);
        setResponse(null);
      }
    } catch (error) {
      // Handle error response based on the status code
      if (error.response && error.response.status === 400) {
        setError('Email already registered');
      } else {
        setError('Registration failed: ' + error.message);
      }
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const savedThemeMode = localStorage.getItem('themeMode') || 'dark';
    setThemeMode(savedThemeMode);
  }, []);
  const toggleTheme = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const theme = createTheme({
    palette: {
      mode: themeMode,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: themeMode === 'light'
            ? 'linear-gradient(135deg, #975cd7, #5a8fea)'
            : 'linear-gradient(135deg, #121212, #333)',
          backgroundSize: 'cover',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <img
            src="/assets/logo.png"
            alt="Logo"
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain',
            }}
          />
        </div>
        <div style={{ position: 'absolute', top: 20, right: 20 }}>
          <IconButton onClick={toggleTheme} style={{ color: '#fff' }}>
            {themeMode === 'light' ? (
              <DarkModeOutlinedIcon />
            ) : (
              <LightModeOutlinedIcon />
            )}
          </IconButton>
        </div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(10px)',
            zIndex: -1,
          }}
        ></div>

        <div
          style={{
            background: themeMode === 'light'
              ? 'rgba(255, 255, 255, 0.3)'
              : 'rgba(0, 0, 0, 0.5)',
            padding: '30px',
            borderRadius: '15px',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.18)',
          }}
        >
          <h2
            style={{
              marginBottom: '20px',
              color: themeMode === 'light' ? '#333' : '#fff',
              fontWeight: 'bold',
            }}
          >
            Register
          </h2>
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              name="email"
              placeholder="📧  Email"
              value={formData.email}
              onChange={handleChange}
              style={{
                margin: '10px 0',
                padding: '12px',
                width: '100%',
                border: '1px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '16px',
                transition: '0.3s',
                background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                color: themeMode === 'light' ? '#333' : '#fff',
              }}
              onFocus={(e) => (e.target.style.border = '2px solid #fda085')}
              onBlur={(e) => (e.target.style.border = '1px solid #ddd')}
              required
            />
            <input
              type="text"
              name="name"
              placeholder="📝  Name"
              value={formData.name}
              onChange={handleChange}
              style={{
                margin: '10px 0',
                padding: '12px',
                width: '100%',
                border: '1px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '16px',
                transition: '0.3s',
                background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                color: themeMode === 'light' ? '#333' : '#fff',
              }}
              onFocus={(e) => (e.target.style.border = '2px solid #fda085')}
              onBlur={(e) => (e.target.style.border = '1px solid #ddd')}
              required
            />
            <input
              type="text"
              name="username"
              placeholder="👤  Username"
              value={formData.username}
              onChange={handleChange}
              style={{
                margin: '10px 0',
                padding: '12px',
                width: '100%',
                border: '1px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '16px',
                transition: '0.3s',
                background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                color: themeMode === 'light' ? '#333' : '#fff',
              }}
              onFocus={(e) => (e.target.style.border = '2px solid #fda085')}
              onBlur={(e) => (e.target.style.border = '1px solid #ddd')}
              required
            />
            <input
              type="text"
              name="role"
              placeholder="👨‍💻 Role (e.g., influencer, admin)"
              value={formData.role}
              onChange={handleChange}
              style={{
                margin: '10px 0',
                padding: '12px',
                width: '100%',
                border: '1px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '16px',
                transition: '0.3s',
                background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                color: themeMode === 'light' ? '#333' : '#fff',
              }}
              onFocus={(e) => (e.target.style.border = '2px solid #fda085')}
              onBlur={(e) => (e.target.style.border = '1px solid #ddd')}
              required
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: '10px',
                padding: '12px',
                background: themeMode === 'light'
                  ? 'linear-gradient(90deg, #975cd7, #5a8fea)'
                  : 'linear-gradient(90deg, #333, #121212)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: '0.3s ease',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={(e) => (e.target.style.transform = 'scale(1.05)')}
              onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
          <p
            style={{
              marginTop: '15px',
              color: themeMode === 'light' ? '#D7127A' : '#E57373',
            }}
          >
            Already have an account?{' '}
            <Link
              to="/login"
              style={{
                color: themeMode === 'light' ? '#444444' : '#ffffff',
                textDecoration: 'none',
                fontWeight: 'bold',
              }}
            >
              Login here
            </Link>
          </p>
          {response && (
            <div style={{ marginTop: '10px', color: '#fff' }}>
              <p>Message: {response.message}</p>
              <p>Temporary ID: {response.tempID}</p>
            </div>
          )}
          {error && (
            <p style={{ color: 'red', marginTop: '11px' }}>{error}</p>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default RegisterForm;

